import "./countdown.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';

export class Countdown {
  private static registry = new ComponentRegistry<Countdown>('countdown');
  private readonly id: string;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly targetDate: Date;
  private readonly numberEls: HTMLSpanElement[];

  private constructor(
    private readonly input: HTMLInputElement,
    private readonly display: HTMLElement
  ) {
    this.id = Countdown.registry.register(input, this);
    this.targetDate = new Date(input.value);
    this.numberEls = Array.from(this.display.querySelectorAll('.countdown-number'));
    this.tick();
    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  private tick(): void {
    const diff = this.targetDate.getTime() - Date.now();

    if (diff <= 0) {
      this.numberEls.forEach(el => el.textContent = '0');
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const values = [days, hours, minutes, seconds];
    this.numberEls.forEach((el, i) => {
      el.textContent = String(values[i]);
    });
  }

  public static create(
    target: string | HTMLInputElement | NodeListOf<HTMLElement> | HTMLElement[]
  ): Countdown[] | null {
    const inputs = ComponentRegistry.processTargets<Countdown, HTMLElement>(
      Countdown.registry,
      target,
      [HTMLInputElement]
    ) as HTMLInputElement[];

    if (!inputs?.length) return null;

    return inputs.map(input => {
      const displaySelector = input.dataset.countdownDisplay;
      if (!displaySelector) {
        throw new Error('Countdown: Input is missing data-countdown-display attribute');
      }

      const display = document.querySelector<HTMLElement>(displaySelector);
      if (!display) {
        throw new Error(`Countdown: Display element not found for selector "${displaySelector}"`);
      }

      return new Countdown(input, display);
    });
  }

  public static getCountdown(id: string): Countdown | null {
    return Countdown.registry.get(id) ?? null;
  }

  public destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    Countdown.registry.unregister(this.id);
  }
}